import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from "react";
import { cn } from "../helpers/class-names";

export type ActionIconColor =
  | "default"
  | "accent"
  | "danger";
export type ActionIconSize = "compact" | "small" | "medium";

type ActionIconCommonProps = {
  color?: ActionIconColor;
  icon: ReactNode;
  label: string;
  size?: ActionIconSize;
};

type ActionIconProps = ActionIconCommonProps &
  (
    | ({ href: string } & Omit<
      AnchorHTMLAttributes<HTMLAnchorElement>,
        "children" | "color" | "href" | "type"
      >)
    | ({ href?: never } & Omit<
        ButtonHTMLAttributes<HTMLButtonElement>,
        "children" | "color"
      >)
  );

export function ActionIcon(props: ActionIconProps) {
  const {
    color = "default",
    label,
    size = "medium",
    className,
  } = props;
  const classes = cn(
    "ui-action-icon",
    `ui-action-icon--${color}`,
    `ui-action-icon--${size}`,
    className,
  );

  if (typeof props.href === "string") {
    const {
      href,
      icon,
      label: _label,
      color: _color,
      size: _size,
      className: _className,
      title = label,
      ...linkProps
    } = props;

    return (
      <a
        aria-label={label}
        className={classes}
        href={href}
        title={title}
        {...linkProps}
      >
        {icon}
      </a>
    );
  }

  const {
    icon,
    label: _label,
    color: _color,
    size: _size,
    className: _className,
    title = label,
    type = "button",
    href: _href,
    ...buttonProps
  } = props;

  return (
    <button
      aria-label={label}
      className={classes}
      title={title}
      type={type}
      {...buttonProps}
    >
      {icon}
    </button>
  );
}
